import { defineStore } from 'pinia'
import { ref } from 'vue'
import { useAuthStore } from '@stores/AuthStore.js'

/**
 * What's included in the subscriber's plan (SUB-MVP-37). Loads the entitlement
 * list from finder (GET /api/client/included-services): the free services their
 * plan covers plus the Business Name discount. Each item deep-links to its normal
 * register flow, where finder applies the free/discount pricing and records the
 * claim. Not persisted — re-fetched on every visit. Mirrors SubscriptionStore's
 * Bearer auth + error handling.
 */
export const useIncludedServicesStore = defineStore('includedServicesStore', () => {
  const authStore = useAuthStore()

  const items = ref([])
  const isSubscriber = ref(false)
  const loading = ref(false)
  const loadError = ref(false)

  const headers = () => ({ Authorization: `Bearer ${authStore.token}` })

  const handleAuthError = (error) => {
    if ([401, 403].includes(error?.response?.status)) {
      authStore.clearSession()
      if (!import.meta.env.SSR) window.location.href = '/'
      return true
    }
    return false
  }

  const load = async () => {
    loading.value = true
    loadError.value = false
    try {
      const { data } = await axios.get('/api/client/included-services', { headers: headers() })
      isSubscriber.value = !!data.is_subscriber
      items.value = data.items ?? []
    } catch (error) {
      if (!handleAuthError(error)) loadError.value = true
    } finally {
      loading.value = false
    }
  }

  const reset = () => {
    items.value = []
    isSubscriber.value = false
    loading.value = false
    loadError.value = false
  }

  return {
    items,
    isSubscriber,
    loading,
    loadError,
    load,
    reset,
  }
})
